import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import type { Dataset, Page, PublicNotice } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { PageHeader, StatCard } from '../../ui/components';

export function StaffHome() {
  const { persona } = useAuth();
  const datasetsQ = useApiQuery<Page<Dataset>>('/api/v1/opengov/datasets?limit=100');
  const noticesQ = useApiQuery<Page<PublicNotice>>('/api/v1/opengov/notices?limit=100');

  const stewardMdas = (persona?.roles ?? [])
    .filter((r) => r.startsWith('DATA_STEWARD:'))
    .map((r) => r.split(':')[1]);
  const isFoiOfficer = persona?.roles.includes('FOI_OFFICER') ?? false;

  return (
    <div>
      <PageHeader title={`Welcome, ${persona?.name}`} subtitle={`${persona?.title} · ${persona?.org}`} />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <StatCard label="Datasets published" value={datasetsQ.data?.items.length ?? '—'} hint="Across all MDAs" />
        <StatCard label="Public notices" value={noticesQ.data?.items.length ?? '—'} hint="Across all sources" />
        <StatCard
          label="Your access"
          value={persona?.roles.length ?? 0}
          hint={persona?.roles.join(', ') || 'No roles granted'}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Link to="/staff/foi" className="card p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md">
          <h3 className="text-base font-bold text-slate-900">FOI requests</h3>
          <p className="mt-1 text-sm text-slate-500">
            {isFoiOfficer
              ? 'Look up a request by reference number to assign it to yourself or respond.'
              : 'Your account does not hold the FOI_OFFICER role.'}
          </p>
        </Link>
        <Link to="/staff/datasets" className="card p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md">
          <h3 className="text-base font-bold text-slate-900">Datasets &amp; budget data</h3>
          <p className="mt-1 text-sm text-slate-500">
            {stewardMdas.length > 0
              ? `Publish datasets and ingest budget lines for ${stewardMdas.join(', ')}.`
              : 'Your account does not hold a DATA_STEWARD role.'}
          </p>
        </Link>
        <Link to="/staff/notices" className="card p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md">
          <h3 className="text-base font-bold text-slate-900">Public notices</h3>
          <p className="mt-1 text-sm text-slate-500">Publish a gazette notice, tender, planning application or licence.</p>
        </Link>
      </div>
    </div>
  );
}
